import { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Calendar } from '../ui/calendar';
import { disablePastDates, makeDisableBeforeDateChargement } from '../../lib/dateConstraints';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { AddressAutocompleteInput } from './AddressAutocompleteInput';
import { ReadOnlyGuard } from '../permissions/ReadOnlyGuard';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../ui/alert-dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../ui/select';
import { Loader2, CalendarIcon, User, Globe, Building, MapPin, Truck, FileText, CheckCircle2, Sparkles, Mail, Clock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useEditLock } from '../../hooks/useEditLock';
import { LockBanner } from '../permissions/LockBanner';

const API = process.env.REACT_APP_BACKEND_URL;

// Status colors
const STATUS_COLORS = {
    nouveau: 'bg-blue-100 text-blue-700',
    sans_reponse_1: 'bg-orange-100 text-orange-700',
    sans_reponse_2: 'bg-orange-100 text-orange-700',
    sans_reponse_3: 'bg-orange-100 text-orange-700',
    a_rappeler: 'bg-yellow-100 text-yellow-700',
    devis_estimatif: 'bg-violet-100 text-violet-700',
    listing_en_attente: 'bg-slate-100 text-slate-700',
    listing_telephone: 'bg-violet-100 text-violet-700',
    listing_email: 'bg-indigo-100 text-indigo-700',
    visite: 'bg-cyan-100 text-cyan-700',
    faux_contact: 'bg-red-100 text-red-700',
    non_interessant: 'bg-red-100 text-red-600'
};

export function ProspectFormDialog({ open, onOpenChange, prospect, config, onSuccess, prefillAdresseDepart }) {
    const isEditing = !!prospect;
    const lock = useEditLock('prospect', isEditing ? prospect?.id : null, isEditing && open);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [initialFormData, setInitialFormData] = useState(null);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [isCorrecting, setIsCorrecting] = useState(false);
    
    const [formData, setFormData] = useState({
        client_nom: '',
        telephone: '',
        email: '',
        source: '',
        departement: '',
        statut: 'nouveau',
        remarques: '',
        adresse_depart: {
            date: null,
            adresse: '',
            etage: '',
            ascenseur: '',
            portage: '',
            autorisation_stationnement: null,
            monte_meuble: null
        },
        adresse_arrivee: {
            date: null,
            adresse: '',
            etage: '',
            ascenseur: '',
            portage: '',
            autorisation_stationnement: null,
            monte_meuble: null
        }
    });

    useEffect(() => {
        if (open) {
            let data;
            if (prospect) {
                data = {
                    client_nom: prospect.client_nom || '',
                    telephone: prospect.telephone || '',
                    email: prospect.email || '',
                    source: prospect.source || '',
                    departement: prospect.departement || '',
                    statut: prospect.statut || 'nouveau',
                    remarques: prospect.remarques || '',
                    adresse_depart: prospect.adresse_depart || {
                        date: null, adresse: '', etage: '', ascenseur: '', portage: '',
                        autorisation_stationnement: null, monte_meuble: null
                    },
                    adresse_arrivee: prospect.adresse_arrivee || {
                        date: null, adresse: '', etage: '', ascenseur: '', portage: '',
                        autorisation_stationnement: null, monte_meuble: null
                    }
                };
            } else {
                data = {
                    client_nom: '', telephone: '', email: '', source: '', departement: '', statut: 'nouveau', remarques: '',
                    adresse_depart: { date: null, adresse: prefillAdresseDepart || '', etage: '', ascenseur: '', portage: '', autorisation_stationnement: null, monte_meuble: null },
                    adresse_arrivee: { date: null, adresse: '', etage: '', ascenseur: '', portage: '', autorisation_stationnement: null, monte_meuble: null }
                };
            }
            setFormData(data);
            setInitialFormData(JSON.stringify(data));
        }
    }, [open, prospect, prefillAdresseDepart]);

    if (lock.blocked) {
        // Verrouillé par un autre utilisateur : on laisse le dialog s'afficher en
        // lecture seule avec la LockBanner (même comportement que les autres formulaires).
    }

    const hasChanges = () => {
        if (!initialFormData) return false;
        return JSON.stringify(formData) !== initialFormData;
    };

    const handleCancel = () => {
        if (hasChanges()) {
            setShowCancelConfirm(true);
        } else {
            onOpenChange(false);
        }
    };

    // Validation helpers for section status
    const isSectionComplete = {
        statut: () => !!formData.statut,
        infos: () => !!formData.client_nom.trim() && !!formData.telephone.trim() && !!formData.email.trim(),
        source: () => !!formData.source,
        departement: () => !!formData.departement,
        adresseDepart: () => {
            const a = formData.adresse_depart;
            return !!a.date && !!a.adresse.trim() && !!a.etage.trim() && !!a.ascenseur && !!a.portage.trim() && a.autorisation_stationnement !== null && a.monte_meuble !== null;
        },
        adresseArrivee: () => {
            const a = formData.adresse_arrivee;
            return !!a.date && !!a.adresse.trim() && !!a.etage.trim() && !!a.ascenseur && !!a.portage.trim() && a.autorisation_stationnement !== null && a.monte_meuble !== null;
        },
        remarques: () => !!formData.remarques.trim()
    };

    const getSectionBorderClass = (isComplete) => {
        return ''; // no card border, line is internal
    };

    const SectionLine = ({ isComplete }) => (
        <div className={`w-1 rounded-full self-stretch shrink-0 ${isComplete ? 'bg-emerald-500' : 'bg-red-400'}`} />
    );

    const SectionCheck = ({ isComplete }) => (
        isComplete ? <CheckCircle2 size={16} className="text-emerald-500 shrink-0" /> : null
    );

    const updateAddress = (type, field, value) => {
        setFormData(prev => ({
            ...prev,
            [type]: { ...prev[type], [field]: value }
        }));
    };

    const buildConfirmationMessage = () => '';  // deprecated — déplacé vers ConfirmationRDVDialog

    const initEmailFields = () => {};  // deprecated



    const saveAndClose = async () => {
        if (!formData.client_nom.trim()) {
            toast.error('Le nom est obligatoire');
            setShowCancelConfirm(false);
            return;
        }
        if (!formData.departement) {
            toast.error('Le département est obligatoire');
            setShowCancelConfirm(false);
            return;
        }

        setIsSubmitting(true);
        try {
            const payload = {
                ...formData,
                source: formData.source || null,
                adresse_depart: formData.adresse_depart.adresse ? formData.adresse_depart : null,
                adresse_arrivee: formData.adresse_arrivee.adresse ? formData.adresse_arrivee : null
            };

            if (isEditing) {
                await axios.put(`${API}/api/prospects/${prospect.id}`, payload, { withCredentials: true });
                toast.success('Prospect modifié avec succès');
            } else {
                const { data: newProspect } = await axios.post(`${API}/api/prospects`, payload, { withCredentials: true });
                toast.success('Prospect créé avec succès');
                setShowCancelConfirm(false);
                onSuccess(newProspect);
                return;
            }
            setShowCancelConfirm(false);
            onSuccess();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
            setShowCancelConfirm(false);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.client_nom.trim()) {
            toast.error('Le nom est obligatoire');
            return;
        }
        if (!formData.departement) {
            toast.error('Le département est obligatoire');
            return;
        }

        setIsSubmitting(true);
        try {
            const payload = {
                ...formData,
                source: formData.source || null,
                adresse_depart: formData.adresse_depart.adresse ? formData.adresse_depart : null,
                adresse_arrivee: formData.adresse_arrivee.adresse ? formData.adresse_arrivee : null
            };

            if (isEditing) {
                await axios.put(`${API}/api/prospects/${prospect.id}`, payload, { withCredentials: true });
                toast.success('Prospect modifié avec succès');
                onSuccess();
            } else {
                const { data: newProspect } = await axios.post(`${API}/api/prospects`, payload, { withCredentials: true });
                toast.success('Prospect créé avec succès');
                onSuccess(newProspect);
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
        } finally {
            setIsSubmitting(false);
        }
    };

    const correctText = async () => {
        if (!formData.remarques?.trim()) return;
        setIsCorrecting(true);
        try {
            const res = await axios.post(`${API}/api/ai/correct-text`, { text: formData.remarques }, { withCredentials: true });
            setFormData(prev => ({ ...prev, remarques: res.data.corrected_text }));
            toast.success('Texte corrigé');
        } catch (err) {
            toast.error('Erreur lors de la correction');
        } finally { setIsCorrecting(false); }
    };

    const DatePickerField = ({ label, value, onChange, disabledFn }) => {
        // Parse YYYY-MM-DD (ou ISO complet) en date locale pour éviter décalage fuseau
        const dateValue = value ? (() => {
            const s = String(value).slice(0, 10);
            const [y, m, d] = s.split('-').map(Number);
            return (y && m && d) ? new Date(y, m - 1, d) : null;
        })() : null;
        const toLocalISO = (d) => {
            if (!d) return null;
            const y = d.getFullYear();
            const mo = String(d.getMonth() + 1).padStart(2, '0');
            const da = String(d.getDate()).padStart(2, '0');
            return `${y}-${mo}-${da}`;
        };
        return (
            <div className="flex items-center gap-4">
                <Label className="text-slate-600 w-28 md:w-72 shrink-0">{label}</Label>
                <Popover>
                    <PopoverTrigger asChild>
                        <Button variant="outline" className="max-w-sm ml-auto w-full justify-start text-left font-normal">
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {dateValue ? format(dateValue, 'dd/MM/yyyy', { locale: fr }) : 'Sélectionner une date'}
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                            mode="single"
                            selected={dateValue}
                            onSelect={(date) => onChange(toLocalISO(date))}
                            locale={fr}
                            disabled={disabledFn || disablePastDates}
                        />
                    </PopoverContent>
                </Popover>
            </div>
        );
    };

    const renderAddressSection = (title, type, data, Icon, isComplete) => (
        <Card className="border-slate-200" key={type}>
            <CardHeader className="pb-3 border-b border-slate-100">
                <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                    <Icon size={16} className="text-[#2e7cd6]" />
                    {title}
                    <SectionCheck isComplete={isComplete} />
                </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
                <div className="flex gap-4">
                    <SectionLine isComplete={isComplete} />
                    <div className="flex-1 space-y-4">
                <DatePickerField
                    label={type === 'adresse_depart' ? 'Date de chargement' : 'Date de déchargement'}
                    value={data.date}
                    onChange={(val) => updateAddress(type, 'date', val)}
                    disabledFn={
                        type === 'adresse_arrivee'
                            ? makeDisableBeforeDateChargement(formData.adresse_depart?.date)
                            : disablePastDates
                    }
                />

                <div className="flex items-center gap-4">
                    <Label className="text-slate-600 w-28 md:w-72 shrink-0">Adresse</Label>
                    <AddressAutocompleteInput
                        className="max-w-sm ml-auto"
                        value={data.adresse || ''}
                        onChange={(val) => updateAddress(type, 'adresse', val)}
                        placeholder="Saisir l'adresse complète"
                    />
                </div>

                <div className="flex items-center gap-4">
                    <Label className="text-slate-600 w-28 md:w-72 shrink-0">Étage</Label>
                    <Input
                        className="max-w-sm ml-auto"
                        value={data.etage || ''}
                        onChange={(e) => updateAddress(type, 'etage', e.target.value)}
                        placeholder="Ex: 3ème"
                    />
                </div>

                <div className="flex items-center gap-4">
                    <Label className="text-slate-600 w-28 md:w-72 shrink-0">Portage</Label>
                    <Input
                        className="max-w-sm ml-auto"
                        value={data.portage || ''}
                        onChange={(e) => updateAddress(type, 'portage', e.target.value)}
                        placeholder="Ex: 50m"
                    />
                </div>

                <div className="flex items-center gap-4">
                    <Label className="text-slate-600 w-28 md:w-72 shrink-0">Ascenseur</Label>
                    <Select
                        value={data.ascenseur || undefined}
                        onValueChange={(val) => updateAddress(type, 'ascenseur', val)}
                    >
                        <SelectTrigger className="max-w-sm ml-auto">
                            <SelectValue placeholder="Sélectionner" />
                        </SelectTrigger>
                        <SelectContent>
                            {config.ascenseur_options.map((opt) => (
                                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <div className="flex items-center gap-4">
                    <Label className="text-slate-600 w-28 md:w-72 shrink-0">Autorisation stationnement</Label>
                    <Select
                        value={data.autorisation_stationnement === true ? 'oui' : data.autorisation_stationnement === false ? 'non' : ''}
                        onValueChange={(val) => updateAddress(type, 'autorisation_stationnement', val === 'oui' ? true : val === 'non' ? false : null)}
                    >
                        <SelectTrigger className="max-w-sm ml-auto">
                            <SelectValue placeholder="Sélectionner" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="oui">Oui</SelectItem>
                            <SelectItem value="non">Non</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                <div className="flex items-center gap-4">
                    <Label className="text-slate-600 w-28 md:w-72 shrink-0">Monte-meuble</Label>
                    <Select
                        value={data.monte_meuble === true ? 'oui' : data.monte_meuble === false ? 'non' : ''}
                        onValueChange={(val) => updateAddress(type, 'monte_meuble', val === 'oui' ? true : val === 'non' ? false : null)}
                    >
                        <SelectTrigger className="max-w-sm ml-auto">
                            <SelectValue placeholder="Sélectionner" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="oui">Oui</SelectItem>
                            <SelectItem value="non">Non</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );

    return (
        <>
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-5xl w-full max-h-[90dvh] flex flex-col p-0 overflow-hidden"
                data-testid="prospect-form-dialog"
                onPointerDownOutside={(e) => e.preventDefault()}
                onEscapeKeyDown={(e) => e.preventDefault()}
                hideCloseButton
            >
                {lock.blocked && <LockBanner userName={lock.user_name} expiresAt={lock.expires_at} onClose={() => onOpenChange(false)} />}
                <ReadOnlyGuard module="prospects" lockBlocked={lock.blocked}>
                {/* Sticky Header */}
                <div className="sticky top-0 z-50 bg-white border-b px-4 md:px-6 py-3 md:py-4 flex items-center justify-between flex-wrap gap-2">
                    <div>
                        <DialogTitle className="text-base md:text-xl font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            {isEditing ? 'Modifier le prospect' : 'Nouveau prospect'}
                        </DialogTitle>
                        <DialogDescription className="text-sm text-slate-500">
                            {isEditing ? 'Modifiez les informations du prospect' : 'Créez un nouveau prospect'}
                        </DialogDescription>
                    </div>
                    <div className="flex items-center gap-3">
                        <Button type="button" variant="outline" onClick={handleCancel}>
                            Annuler
                        </Button>
                        <Button
                            type="submit"
                            form="prospect-form"
                            className="bg-[#2e7cd6] hover:bg-blue-700"
                            disabled={isSubmitting}
                            data-testid="prospect-submit"
                        >
                            {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                            {isEditing ? 'Enregistrer' : 'Créer'}
                        </Button>
                    </div>
                </div>

                {/* Scrollable Content */}
                <div className="flex-1 min-h-0 overflow-y-auto px-4 md:px-6 py-4">
                    <form id="prospect-form" onSubmit={handleSubmit} className="space-y-6">
                    {/* Section 1: Statut (only in edit mode) */}
                    {isEditing && (
                    <Card className="bg-blue-50 border-blue-100">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-semibold text-blue-800">
                                1. Statut du prospect
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <Select
                                value={formData.statut}
                                onValueChange={(val) => setFormData({ ...formData, statut: val })}
                            >
                                <SelectTrigger className="w-full md:w-[300px] bg-white" data-testid="prospect-statut">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {config.statuses.map((s) => (
                                        <SelectItem key={s.value} value={s.value}>
                                            <span className={`inline-block px-2 py-0.5 rounded text-sm ${STATUS_COLORS[s.value] || ''}`}>
                                                {s.label}
                                            </span>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </CardContent>
                    </Card>
                    )}

                    {/* Section: Informations générales */}
                    <Card className="border-slate-200">
                        <CardHeader className="pb-3 border-b border-slate-100">
                            <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                                <User size={16} className="text-[#2e7cd6]" />
                                {isEditing ? '2' : '1'}. Informations générales
                                <SectionCheck isComplete={isSectionComplete.infos()} />
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-4">
                            <div className="flex gap-4">
                                <SectionLine isComplete={isSectionComplete.infos()} />
                                <div className="flex-1 space-y-4">
                                    <div className="flex items-center gap-4">
                                        <Label className="text-slate-600 w-28 md:w-72 shrink-0">Nom *</Label>
                                        <Input
                                            className="max-w-sm ml-auto"
                                            value={formData.client_nom}
                                            onChange={(e) => setFormData({ ...formData, client_nom: e.target.value })}
                                            placeholder="Nom du prospect"
                                            required
                                            data-testid="prospect-nom"
                                        />
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <Label className="text-slate-600 w-28 md:w-72 shrink-0">Téléphone</Label>
                                        <Input
                                            className="max-w-sm ml-auto"
                                            value={formData.telephone}
                                            onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                                            placeholder="06 00 00 00 00"
                                            data-testid="prospect-telephone"
                                        />
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <Label className="text-slate-600 w-28 md:w-72 shrink-0">Email</Label>
                                        <Input
                                            className="max-w-sm ml-auto"
                                            type="email"
                                            value={formData.email}
                                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                            placeholder="email@exemple.fr"
                                            data-testid="prospect-email"
                                        />
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Section: Source & Département */}
                    <Card className="border-slate-200">
                        <CardHeader className="pb-3 border-b border-slate-100">
                            <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                                <Globe size={16} className="text-[#2e7cd6]" />
                                {isEditing ? '3' : '2'}. Source & Département
                                <SectionCheck isComplete={isSectionComplete.source() && isSectionComplete.departement()} />
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-4">
                            <div className="flex gap-4">
                                <SectionLine isComplete={isSectionComplete.source() && isSectionComplete.departement()} />
                                <div className="flex-1 space-y-4">
                                    <div className="flex items-center gap-4">
                                        <Label className="text-slate-600 w-28 md:w-72 shrink-0">Source</Label>
                                        <Select
                                            value={formData.source || ''}
                                            onValueChange={(val) => setFormData({ ...formData, source: val })}
                                        >
                                            <SelectTrigger className="max-w-sm ml-auto" data-testid="prospect-source">
                                                <SelectValue placeholder="Sélectionner une source" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {config.sources.map((s) => (
                                                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                                ))}
                                                {formData.source && !config.sources.some(s => s.value === formData.source) && (
                                                    <SelectItem value={formData.source}>{formData.source} (ancien)</SelectItem>
                                                )}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <Label className="text-slate-600 w-28 md:w-72 shrink-0">Département *</Label>
                                        <Select
                                            value={formData.departement || ''}
                                            onValueChange={(val) => setFormData({ ...formData, departement: val })}
                                        >
                                            <SelectTrigger className="max-w-sm ml-auto" data-testid="prospect-departement">
                                                <SelectValue placeholder="Sélectionner un département" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {config.departements.map((d) => (
                                                    <SelectItem key={d.code} value={d.code}>{d.label}</SelectItem>
                                                ))}
                                                {formData.departement && !config.departements.some(d => d.code === formData.departement) && (
                                                    <SelectItem value={formData.departement}>{formData.departement}</SelectItem>
                                                )}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Section: Adresse de départ */}
                    {renderAddressSection(
                        `${isEditing ? '4' : '3'}. Adresse de départ`,
                        'adresse_depart',
                        formData.adresse_depart,
                        MapPin,
                        isSectionComplete.adresseDepart()
                    )}

                    {/* Section: Adresse d'arrivée */}
                    {renderAddressSection(
                        `${isEditing ? '5' : '4'}. Adresse d'arrivée`,
                        'adresse_arrivee',
                        formData.adresse_arrivee,
                        Truck,
                        isSectionComplete.adresseArrivee()
                    )}

                    {/* Section 7: Remarques */}
                    <Card className="border-slate-200">
                        <CardHeader className="pb-3 border-b border-slate-100">
                            <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                                <FileText size={16} className="text-[#2e7cd6]" />
                                {isEditing ? '6' : '5'}. Remarques
                                <SectionCheck isComplete={isSectionComplete.remarques()} />
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={correctText}
                                    disabled={isCorrecting || !formData.remarques?.trim()}
                                    className="gap-2 ml-auto border-[#2e7cd6] text-[#2e7cd6] hover:bg-[#2e7cd6]/10"
                                    data-testid="correct-text-btn"
                                >
                                    {isCorrecting ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                                    {isCorrecting ? 'Correction...' : 'Corriger'}
                                </Button>
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-4">
                            <div className="flex gap-4">
                                <SectionLine isComplete={isSectionComplete.remarques()} />
                                <div className="flex-1">
                                    <Textarea
                                        value={formData.remarques}
                                        onChange={(e) => setFormData({ ...formData, remarques: e.target.value })}
                                        placeholder="Notes et remarques..."
                                        rows={6}
                                        className="w-full min-h-[120px] resize-y"
                                        data-testid="prospect-remarques"
                                    />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    </form>
                </div>
                </ReadOnlyGuard>
            </DialogContent>
        </Dialog>

        <AlertDialog open={showCancelConfirm} onOpenChange={setShowCancelConfirm}>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>
                        {isEditing ? 'Modifications non enregistrées' : 'Abandonner la création ?'}
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                        {isEditing
                            ? 'Vous avez des modifications non enregistrées. Voulez-vous les enregistrer avant de quitter ?'
                            : 'Voulez-vous vraiment abandonner la création de ce prospect ? Les informations saisies seront perdues.'
                        }
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    {isEditing ? (
                        <>
                            <AlertDialogCancel onClick={() => {
                                setShowCancelConfirm(false);
                                onOpenChange(false);
                            }}>
                                Ne pas enregistrer
                            </AlertDialogCancel>
                            <AlertDialogAction onClick={saveAndClose}>
                                Enregistrer
                            </AlertDialogAction>
                        </>
                    ) : (
                        <>
                            <AlertDialogCancel onClick={() => setShowCancelConfirm(false)}>
                                Continuer la création
                            </AlertDialogCancel>
                            <AlertDialogAction onClick={() => {
                                setShowCancelConfirm(false);
                                onOpenChange(false);
                            }}>
                                Abandonner
                            </AlertDialogAction>
                        </>
                    )}
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
        </>
    );
}
